/* eslint-disable max-len */
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Alert, Card, Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { getIsinList } from 'services/akcionar/emise/novyIsinSend';
import sezZakniEmisiApi, { ISezZakEmisi } from 'services/akcionar/emise/seznamZakEmisi';
import vlastneneEmiseApi from 'services/akcionar/emise/vlastneneEmise';
import profilyApi from 'services/profily';
import EmitentIsins, {
    ISezIsinu,
    IZmenaUctuData,
    zmenitUcetEmitentApi
} from 'services/spravaEmitenta/SpravaEmitentaIsins';
import historickeZadostiApi from 'services/spravaEmitenta/historickeZadosti';
import { zadostPravoCheck } from 'services/spravaEmitenta/zadostPravo';
import { EStavUzivatele } from 'services/spravaUzivatelu/uzivatele';

import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

interface ISpravaEmitentaPrehled {}

const SpravaEmitentaPrehled: React.FC<ISpravaEmitentaPrehled> = () => {
    const [form] = Form.useForm();
    const { aktivniFirma, menuProfile, NotificationComponent } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const [loading, setLoading] = useState<boolean>(false);
    const [zakEmise, setZakEmise] = useState<ISezZakEmisi[]>([]);
    const [zakEmiseErr, setZakEmiseErr] = useState<boolean>(false);
    const [showChangeModal, setShowChangeModal] = useState<boolean>(false);
    const [isinyTable, setIsinyTable] = useState<ISezIsinu>();
    const [isinyErr, setIsinyErr] = useState<boolean>(false);

    useEffect(() => {
        setLoading(true);
        zadostPravoCheck().then(resp => {
            setLevel('zadostAgenda', resp);
        });

        menuProfile?.amlz !== 'ZADOST' &&
            menuProfile?.amlz !== 'OSOBY' &&
            historickeZadostiApi({
                offset: 0,
                order: 'datum:desc',
                limit: 10
            })
                .then(resp => {
                    const zadost =
                        resp.zadosti &&
                        resp.zadosti.filter(e => (e.typ === 'PRISTUPAMLZ' ? e.stav !== 'HOTOVOIIS' : true))[0];

                    zadost &&
                        setLevel('amlz', {
                            idzadosti: zadost.idzadosti
                        } as IAmlz);
                })
                .finally(() => setLoading(false));
        if (aktivniFirma?.stat === 'CZ' || aktivniFirma?.nid) {
            vlastneneEmiseApi(
                'jmeno:asc',
                'cz',
                aktivniFirma?.stat !== 'CZ' && aktivniFirma?.nid ? aktivniFirma?.nid : aktivniFirma?.ic,
                'E'
            )
                .then(respVlastEmise => {
                    respVlastEmise
                        ? sezZakniEmisiApi(respVlastEmise[0].idsbj ? respVlastEmise[0].idsbj : 0).then(data => {
                              setZakEmise(data);
                          })
                        : setZakEmiseErr(true);
                })
                .catch(() => {
                    setZakEmiseErr(true);
                })
                .finally(() => setLoading(false));
            EmitentIsins(aktivniFirma?.stat !== 'CZ' && aktivniFirma?.nid ? aktivniFirma?.nid : aktivniFirma?.ic || '')
                .then(resp => {
                    setIsinyTable(resp);
                })
                .catch(() => setIsinyErr(true));
        } else {
            setZakEmiseErr(true);
            setIsinyErr(true);
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getIsinList().then(resp => setLevel('showIsinEdit', !!resp.isininfos.length));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper
            loading={loading}
            style={{
                paddingTop: '100px'
            }}
        >
            <>
                {menuProfile?.dluznik && (
                    <Alert
                        message={<TranslateText>DLUZNIK_MSG</TranslateText>}
                        type="error"
                        style={{
                            marginBottom: 16
                        }}
                    />
                )}
                {menuProfile?.neplatneaml && (
                    <Alert
                        message={
                            <TranslateText>
                                {menuProfile.datplatamlz ? 'NEPLATNE_AML_ERR' : 'NEPLATNE_AML_ERR_NODATE'}
                            </TranslateText>
                        }
                        type="error"
                        style={{
                            marginBottom: 16
                        }}
                    />
                )}
                <Row gutter={8}>
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={12}
                        xl={12}
                        style={{
                            marginBottom: 6
                        }}
                    >
                        <Card
                            className="dash-card"
                            title={
                                <Row justify="space-between">
                                    <TranslateText>
                                        {aktivniFirma?.spolecnost ? aktivniFirma.spolecnost : ''}
                                    </TranslateText>
                                    <AnchorComponent onClick={() => setShowChangeModal(true)}>
                                        <TranslateText>ZMENIT</TranslateText>
                                    </AnchorComponent>
                                </Row>
                            }
                        >
                            <Title text={aktivniFirma?.typ === 'UCASTNIK' ? 'CDAC' : 'FORMULARE_IC'} level={4} />
                            <p>{aktivniFirma?.ic}</p>
                            {aktivniFirma?.adresa && (
                                <>
                                    <Title text="FORMULARE_ADRESA" level={4} />
                                    <p>{aktivniFirma?.adresa}</p>
                                </>
                            )}
                            {menuProfile?.datplatamlz && (
                                <>
                                    <Title text="DATUM_PLATNOSTI_AML" level={5} />
                                    <Paragraph
                                        text={dayjs(menuProfile.datplatamlz).format('DD.MM.YYYY')}
                                        className={
                                            dayjs(menuProfile.datplatamlz) < dayjs().add(30, 'day')
                                                ? 'extramessage-text'
                                                : ''
                                        }
                                    />
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
                <Title
                    level={4}
                    text="SEZNAM_EMISI_TEXT"
                    style={{
                        paddingTop: 30,
                        marginBottom: 0
                    }}
                />
                <Row gutter={8}>
                    <Col span={24}>
                        <TableComponent
                            pagination={false}
                            columns={
                                zakEmise.length
                                    ? [
                                          {
                                              title: 'ISIN',
                                              key: 'isin',
                                              dataIndex: 'isin'
                                          },
                                          {
                                              title: <TranslateText>STAV</TranslateText>,
                                              key: 'typz',
                                              dataIndex: 'typz',
                                              render: (typz: string | null | undefined) => {
                                                  if (typz === '1') {
                                                      return (
                                                          <span
                                                              style={{
                                                                  color: 'green'
                                                              }}
                                                          >
                                                              <CheckCircleOutlined />
                                                          </span>
                                                      );
                                                  } else if (typz === '2') {
                                                      return (
                                                          <span
                                                              style={{
                                                                  color: 'green'
                                                              }}
                                                          >
                                                              <CheckCircleOutlined />
                                                          </span>
                                                      );
                                                  }
                                                  return (
                                                      <span
                                                          style={{
                                                              color: 'red'
                                                          }}
                                                      >
                                                          <CloseCircleOutlined />
                                                      </span>
                                                  );
                                              }
                                          }
                                      ]
                                    : []
                            }
                            dataSource={zakEmise}
                            loading={zakEmise.length < 1 && !zakEmiseErr}
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                        />
                    </Col>
                </Row>
                <Title
                    level={4}
                    text="EMITENT_PREHLED_ISINY"
                    style={{
                        paddingTop: 30,
                        marginBottom: 0
                    }}
                />
                <Row gutter={8}>
                    <Col span={24}>
                        <TableComponent
                            // pagination={false}
                            columns={
                                isinyTable?.vydaneisiny.length
                                    ? [
                                          {
                                              title: 'ISIN',
                                              key: 'cval',
                                              dataIndex: 'cval'
                                          }
                                      ]
                                    : []
                            }
                            dataSource={isinyTable?.vydaneisiny}
                            loading={!isinyTable && !isinyErr}
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                        />
                    </Col>
                </Row>
            </>
            <Modal
                open={showChangeModal}
                onOk={() => form.submit()}
                onCancel={() => setShowChangeModal(false)}
                confirmLoading={loading}
                zIndex={1001}
                cancelText={<TranslateText>ZPET</TranslateText>}
            >
                <Title level={4} text="ZMENA_UCTU" />
                <FormWrapper
                    onFinish={vals => {
                        setLoading(true);
                        zmenitUcetEmitentApi(aktivniFirma?.iducet!, vals as IZmenaUctuData)
                            .then(() => {
                                profilyApi()
                                    .then(({ data }) => {
                                        const editedProfile = data.sezpristupu!.filter(
                                            e => e.iducet === aktivniFirma!.iducet
                                        );
                                        if (editedProfile.length) {
                                            setLevel('profily', data);
                                            setLevel('ucty', data.sezpristupu!);
                                            setLevel('aktivniFirma', editedProfile[0]);
                                        } else {
                                            NotificationComponent({
                                                title: 'EMITENT_UCET_ZRUSENI_SUCC',
                                                type: ENotificationType.SUCCESS
                                            });
                                            navigateWorkaround(routePathEnum.MAIN_PAGE);
                                        }
                                    })
                                    .finally(() => setLoading(false));
                                setShowChangeModal(false);
                            })
                            .catch(() => setLoading(false));
                    }}
                    outForm={form}
                >
                    <InputComponent
                        label="NAZEV_EMITENTA"
                        name="nazev"
                        type="text"
                        notRequired={true}
                        initialValue={aktivniFirma?.spolecnost}
                    />
                    <SelectComponent
                        isFormItem={true}
                        notRequired={true}
                        labelParagraph="STAV_UCTU"
                        name="stav"
                        defaultValue={aktivniFirma?.stav}
                        data={[
                            {
                                value: EStavUzivatele.AKTIVNI,
                                labelcz: 'UZIVATEL_STAV_A'
                            },
                            {
                                value: EStavUzivatele.NEAKTIVNI,
                                labelcz: 'UZIVATEL_STAV_N'
                            }
                        ]}
                    />
                </FormWrapper>
            </Modal>
        </LoadingWrapper>
    );
};

export default SpravaEmitentaPrehled;
